import type { ScheduleEvent } from './event-block';

/**
 * Result of the overlap layout calculation for a single event.
 */
export interface OverlapLayoutResult {
  /** The event ID this result is for */
  eventId: string;
  /** 0-indexed column position for this event */
  column: number;
  /** Total columns in this time segment (for width calculation) */
  totalColumns: number;
  /** True if this event should show "+N more" indicator */
  isOverflow: boolean;
  /** Number of events hidden behind overflow (only set if isOverflow) */
  overflowCount?: number;
  /** IDs of events hidden behind overflow (only set if isOverflow) */
  overflowEvents?: string[];
}

/**
 * Internal type for working with events during collision detection.
 */
interface EventWithTimes {
  id: string;
  title: string;
  startMs: number;
  endMs: number;
}

/**
 * Check if two events overlap in time.
 * Two events overlap if: event1.start < event2.end && event2.start < event1.end
 */
function eventsOverlap(a: EventWithTimes, b: EventWithTimes): boolean {
  return a.startMs < b.endMs && b.startMs < a.endMs;
}

/**
 * Convert ScheduleEvent to internal format with millisecond timestamps.
 * Returns null if the event has invalid/missing times.
 */
function toEventWithTimes(event: ScheduleEvent): EventWithTimes | null {
  if (!event.start_time || !event.end_time) {
    return null;
  }

  const startMs = new Date(event.start_time).getTime();
  const endMs = new Date(event.end_time).getTime();

  if (isNaN(startMs) || isNaN(endMs)) {
    return null;
  }

  return {
    id: event.id,
    title: event.title || '',
    startMs,
    endMs,
  };
}

/**
 * Find groups of events that overlap with each other transitively.
 * If A overlaps B and B overlaps C, then [A, B, C] form a single collision group.
 *
 * @param events - Array of events sorted by start time
 * @returns Array of collision groups, each containing event IDs
 */
export function findCollisionGroups(
  events: ScheduleEvent[] | undefined
): string[][] {
  if (!events || events.length === 0) {
    return [];
  }

  // Convert to internal format, filtering out invalid events
  const validEvents: EventWithTimes[] = [];
  for (const event of events) {
    const converted = toEventWithTimes(event);
    if (converted) {
      validEvents.push(converted);
    }
  }

  if (validEvents.length === 0) {
    return [];
  }

  // Sort by start time, then by title for deterministic ordering
  validEvents.sort((a, b) => {
    if (a.startMs !== b.startMs) {
      return a.startMs - b.startMs;
    }
    return a.title.localeCompare(b.title);
  });

  // Use union-find to group overlapping events
  const parent = new Map<string, string>();

  // Initialize each event as its own parent
  for (const event of validEvents) {
    parent.set(event.id, event.id);
  }

  // Find with path compression
  function find(id: string): string {
    const p = parent.get(id) ?? id;
    if (p !== id) {
      parent.set(id, find(p));
    }
    return parent.get(id) ?? id;
  }

  // Union two events into the same group
  function union(a: string, b: string): void {
    const rootA = find(a);
    const rootB = find(b);
    if (rootA !== rootB) {
      parent.set(rootB, rootA);
    }
  }

  // Check all pairs for overlap and union them
  for (let i = 0; i < validEvents.length; i++) {
    for (let j = i + 1; j < validEvents.length; j++) {
      // Optimization: if event j starts after event i ends, no need to check further
      // since events are sorted by start time
      if (validEvents[j].startMs >= validEvents[i].endMs) {
        break;
      }
      if (eventsOverlap(validEvents[i], validEvents[j])) {
        union(validEvents[i].id, validEvents[j].id);
      }
    }
  }

  // Group events by their root
  const groups = new Map<string, string[]>();
  for (const event of validEvents) {
    const root = find(event.id);
    if (!groups.has(root)) {
      groups.set(root, []);
    }
    groups.get(root)?.push(event.id);
  }

  return Array.from(groups.values());
}

/**
 * Assign columns to events within a collision group.
 * Uses a greedy approach: iterate events by start time, place in first available column.
 *
 * @param groupEventIds - Event IDs in this collision group
 * @param allEvents - All events (to look up times)
 * @returns Map of eventId -> column number (0-indexed)
 */
export function assignColumns(
  groupEventIds: string[],
  allEvents: ScheduleEvent[]
): Map<string, number> {
  const result = new Map<string, number>();

  if (groupEventIds.length === 0) {
    return result;
  }

  // Build a lookup map for events
  const eventMap = new Map<string, EventWithTimes>();
  for (const event of allEvents) {
    const converted = toEventWithTimes(event);
    if (converted) {
      eventMap.set(event.id, converted);
    }
  }

  // Get valid events in this group
  const groupEvents: EventWithTimes[] = [];
  for (const id of groupEventIds) {
    const event = eventMap.get(id);
    if (event) {
      groupEvents.push(event);
    }
  }

  // Sort by start time, then alphabetically by title for deterministic ordering
  groupEvents.sort((a, b) => {
    if (a.startMs !== b.startMs) {
      return a.startMs - b.startMs;
    }
    return a.title.localeCompare(b.title);
  });

  // Track when each column becomes free (end time of last event in that column)
  const columnEndTimes: number[] = [];

  for (const event of groupEvents) {
    // Find first column where this event can fit
    let assignedColumn = -1;
    for (let col = 0; col < columnEndTimes.length; col++) {
      if (columnEndTimes[col] <= event.startMs) {
        assignedColumn = col;
        break;
      }
    }

    // If no existing column works, create a new one
    if (assignedColumn === -1) {
      assignedColumn = columnEndTimes.length;
      columnEndTimes.push(0);
    }

    // Assign event to this column and update column end time
    result.set(event.id, assignedColumn);
    columnEndTimes[assignedColumn] = event.endMs;
  }

  return result;
}

/**
 * Calculate the complete overlap layout for all events.
 *
 * @param events - Array of ScheduleEvent objects
 * @param maxColumns - Maximum number of visible columns (default 3). Events beyond this
 *                     will be marked as overflow on the last visible column.
 * @returns Array of OverlapLayoutResult for each valid event
 */
export function calculateOverlapLayout(
  events: ScheduleEvent[] | undefined,
  maxColumns: number = 3
): OverlapLayoutResult[] {
  if (!events || events.length === 0) {
    return [];
  }

  const results: OverlapLayoutResult[] = [];
  const collisionGroups = findCollisionGroups(events);

  // Build lookup for quick event access
  const eventMap = new Map<string, ScheduleEvent>();
  for (const event of events) {
    eventMap.set(event.id, event);
  }

  for (const group of collisionGroups) {
    const columnAssignments = assignColumns(group, events);

    // Find the maximum column used in this group
    let maxColumnUsed = 0;
    columnAssignments.forEach(col => {
      maxColumnUsed = Math.max(maxColumnUsed, col);
    });

    const actualColumns = maxColumnUsed + 1;
    const visibleColumns = Math.min(actualColumns, maxColumns);

    // Separate events into visible and overflow
    const visibleEvents: string[] = [];
    const overflowEvents: string[] = [];

    for (const eventId of group) {
      const col = columnAssignments.get(eventId);
      if (col !== undefined && col < maxColumns) {
        visibleEvents.push(eventId);
      } else {
        overflowEvents.push(eventId);
      }
    }

    // Create results for visible events
    for (const eventId of visibleEvents) {
      const col = columnAssignments.get(eventId);
      if (col === undefined) {
        continue;
      }

      // Check if this is the last visible column and has overflow
      const isLastVisibleColumn = col === visibleColumns - 1;
      const hasOverflow = overflowEvents.length > 0 && isLastVisibleColumn;

      results.push({
        eventId,
        column: col,
        totalColumns: visibleColumns,
        isOverflow: hasOverflow,
        ...(hasOverflow && {
          overflowCount: overflowEvents.length,
          overflowEvents: [...overflowEvents],
        }),
      });
    }

    // Overflow events are not included in results (they're hidden)
    // Their info is attached to the last visible column's event
  }

  return results;
}
